/**
 * Walk-Forward Validation tab — Performance Lab.
 *
 * Left rail: saved configurations.
 * Main: configuration editor + run job + progress + window results + summary.
 *
 * Historical integrity is enforced by the backend; this UI faithfully surfaces
 * the data_quality state per window and never hides missing-data flags.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Play, Plus, Save, Trash2, X, Info, AlertTriangle, GitBranch, Repeat } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useConfirm } from "@/components/ConfirmDialog";

const DEFAULT_CFG = {
  name: "Untitled Walk-Forward",
  description: "",
  mode: "rolling",
  start_date: "2025-01-01",
  end_date: "2026-01-01",
  training_days: 90,
  validation_days: 30,
  forward_days: 30,
  step_days: 30,
  min_trades_per_window: 5,
  starting_capital: 100000,
  position_sizing_method: "risk_parity",
  fees_bps: 0,
  slippage_bps: 5,
  benchmark_symbol: "SPY",
  portfolio_key: "",
  strategy_name: "",
  scanner_profile_id: "",
  ai_model: "",
  prompt_version: "",
  market_regime: "",
  sectors: [],
  symbol_universe: [],
  retrospective_mode: false,
};

const PRESETS = {
  Tactical:  { training_days: 90,  validation_days: 30,  forward_days: 30, step_days: 30 },
  Swing:     { training_days: 180, validation_days: 60,  forward_days: 60, step_days: 30 },
  LongTerm:  { training_days: 730, validation_days: 180, forward_days: 180, step_days: 90 },
};

const ROBUSTNESS_TONE = {
  "Insufficient Evidence": "border-border/60 text-muted-foreground",
  "Fragile":               "border-signal-red text-signal-red",
  "Unstable":              "border-signal-yellow text-signal-yellow",
  "Moderately Robust":     "border-[#0A84FF] text-[#0A84FF]",
  "Robust":                "border-signal-green text-signal-green",
};

const STATUS_TONE = {
  "Passed":                  "text-signal-green",
  "Passed with Degradation": "text-signal-yellow",
  "Unstable":                "text-signal-yellow",
  "Failed":                  "text-signal-red",
  "Insufficient Data":       "text-muted-foreground",
  "Not Comparable":          "text-muted-foreground italic",
};

const DQ_TONE = {
  "Available":         "border-signal-green/60 text-signal-green",
  "Partial Data":      "border-signal-yellow/60 text-signal-yellow",
  "Legacy Record":     "border-signal-yellow/60 text-signal-yellow",
  "Insufficient Data": "border-border/60 text-muted-foreground",
  "Not Comparable":    "border-border/60 text-muted-foreground",
  "Unavailable":       "border-signal-red/60 text-signal-red",
};

function pct(v, d = 2) {
  if (v == null || !isFinite(v)) return "N/A";
  return `${v >= 0 ? "+" : ""}${v.toFixed(d)}%`;
}
function num(v, d = 2) {
  if (v == null || !isFinite(v)) return "N/A";
  return v.toFixed(d);
}

function Field({ label, hint, children }) {
  return (
    <div>
      <div className="label-cap mb-1 flex items-center gap-1">
        {label}{hint && <span title={hint} className="text-muted-foreground/70"><Info size={10}/></span>}
      </div>
      {children}
    </div>
  );
}
function NumField({ v, onChange, tid }) {
  return (
    <Input
      value={v ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      inputMode="decimal"
      className="!rounded-none mono text-xs h-8"
      data-testid={`wf-${tid}`}
    />
  );
}

function ConfigList({ configs, activeId, onSelect, onNew, onDelete }) {
  return (
    <div className="grid-cell !p-3 space-y-2" data-testid="wf-config-list">
      <div className="flex items-center gap-2">
        <GitBranch size={12} className="text-muted-foreground"/>
        <span className="mono uppercase text-[10px] tracking-widest text-muted-foreground">Saved Configurations</span>
        <button className="btn-ghost h-6 !px-1 text-[10px] ml-auto" onClick={onNew} data-testid="wf-config-new"><Plus size={11}/>New</button>
      </div>
      {configs.length === 0 && (
        <div className="text-[11px] text-muted-foreground py-2">No configurations yet. Create one to run your first walk-forward.</div>
      )}
      {configs.map((c) => (
        <div key={c.id}
             className={`p-2 border-l-2 cursor-pointer text-xs ${activeId === c.id ? "border-l-[#0A84FF] bg-secondary/40" : "border-l-transparent hover:bg-secondary/25"}`}
             onClick={() => onSelect(c)}
             data-testid={`wf-config-item-${c.id}`}>
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="mono font-medium truncate">{c.name}</div>
              <div className="text-[10px] text-muted-foreground truncate">
                {c.mode} · {c.training_days}/{c.validation_days}/{c.forward_days}d · step {c.step_days}d
              </div>
              <div className="text-[10px] text-muted-foreground truncate">{c.start_date} → {c.end_date}</div>
            </div>
            <button className="text-muted-foreground hover:text-signal-red p-1"
                    onClick={(e) => { e.stopPropagation(); onDelete(c); }}
                    data-testid={`wf-config-delete-${c.id}`}>
              <Trash2 size={11}/>
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ConfigEditor({ cfg, setCfg }) {
  const s = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  return (
    <div className="grid-cell !p-3 space-y-3" data-testid="wf-editor">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="Name"><Input value={cfg.name} onChange={(e) => s("name", e.target.value)} className="!rounded-none mono text-xs h-8" data-testid="wf-name"/></Field>
        <Field label="Description"><Input value={cfg.description} onChange={(e) => s("description", e.target.value)} className="!rounded-none mono text-xs h-8" data-testid="wf-desc"/></Field>
        <Field label="Mode">
          <div className="flex gap-1">
            {["rolling", "expanding"].map((m) => (
              <button key={m}
                onClick={() => s("mode", m)}
                className={`chip cursor-pointer ${cfg.mode === m ? "border-[#0A84FF] text-[#0A84FF]" : "border-border/60 text-muted-foreground"}`}
                data-testid={`wf-mode-${m}`}>{m}</button>
            ))}
          </div>
        </Field>
      </div>

      <div>
        <div className="label-cap mb-1">Presets</div>
        <div className="flex gap-2">
          {Object.entries(PRESETS).map(([name, p]) => (
            <button key={name}
              onClick={() => setCfg((c) => ({ ...c, ...p }))}
              className="chip border-border/60 text-muted-foreground hover:text-foreground cursor-pointer"
              data-testid={`wf-preset-${name}`}>{name}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Start Date"><Input type="date" value={cfg.start_date} onChange={(e) => s("start_date", e.target.value)} className="!rounded-none mono text-xs h-8" data-testid="wf-start"/></Field>
        <Field label="End Date"><Input type="date" value={cfg.end_date} onChange={(e) => s("end_date", e.target.value)} className="!rounded-none mono text-xs h-8" data-testid="wf-end"/></Field>
        <Field label="Training (days)"><NumField v={cfg.training_days} onChange={(v) => s("training_days", v)} tid="train-days"/></Field>
        <Field label="Validation (days)"><NumField v={cfg.validation_days} onChange={(v) => s("validation_days", v)} tid="val-days"/></Field>
        <Field label="Forward Test (days)"><NumField v={cfg.forward_days} onChange={(v) => s("forward_days", v)} tid="fwd-days"/></Field>
        <Field label="Step Size (days)"><NumField v={cfg.step_days} onChange={(v) => s("step_days", v)} tid="step-days"/></Field>
        <Field label="Min Trades / window"><NumField v={cfg.min_trades_per_window} onChange={(v) => s("min_trades_per_window", v)} tid="min-trades"/></Field>
        <Field label="Starting Capital ($)"><NumField v={cfg.starting_capital} onChange={(v) => s("starting_capital", v)} tid="capital"/></Field>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Fees (bps)"><NumField v={cfg.fees_bps} onChange={(v) => s("fees_bps", v)} tid="fees"/></Field>
        <Field label="Slippage (bps)"><NumField v={cfg.slippage_bps} onChange={(v) => s("slippage_bps", v)} tid="slippage"/></Field>
        <Field label="Benchmark">
          <select value={cfg.benchmark_symbol} onChange={(e) => s("benchmark_symbol", e.target.value)}
            className="w-full !rounded-none mono text-xs h-8 px-2 bg-background border border-border/60"
            data-testid="wf-benchmark">
            <option value="SPY">SPY</option>
            <option value="QQQ">QQQ</option>
            <option value="IWM">IWM</option>
          </select>
        </Field>
        <Field label="Portfolio">
          <select value={cfg.portfolio_key || ""} onChange={(e) => s("portfolio_key", e.target.value || null)}
            className="w-full !rounded-none mono text-xs h-8 px-2 bg-background border border-border/60"
            data-testid="wf-portfolio">
            <option value="">All</option>
            <option value="LONG_TERM">LONG_TERM</option>
            <option value="SWING">SWING</option>
            <option value="TACTICAL">TACTICAL</option>
          </select>
        </Field>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label="Strategy Name (filter)"><Input value={cfg.strategy_name || ""} onChange={(e) => s("strategy_name", e.target.value || null)} placeholder="Any" className="!rounded-none mono text-xs h-8" data-testid="wf-strategy"/></Field>
        <Field label="AI Model (filter)"><Input value={cfg.ai_model || ""} onChange={(e) => s("ai_model", e.target.value || null)} placeholder="Any" className="!rounded-none mono text-xs h-8" data-testid="wf-model"/></Field>
        <Field label="Prompt Version (filter)"><Input value={cfg.prompt_version || ""} onChange={(e) => s("prompt_version", e.target.value || null)} placeholder="Any" className="!rounded-none mono text-xs h-8" data-testid="wf-prompt"/></Field>
        <Field label="Market Regime (filter)">
          <select value={cfg.market_regime || ""} onChange={(e) => s("market_regime", e.target.value || null)}
            className="w-full !rounded-none mono text-xs h-8 px-2 bg-background border border-border/60"
            data-testid="wf-regime">
            <option value="">Any</option>
            {["BULL", "NEUTRAL", "BEAR", "VOLATILE", "STABLE", "MIXED"].map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
      </div>

      <div className="flex items-center justify-between border-t border-border/40 pt-2">
        <div className="flex items-center gap-2 text-[11px] text-signal-yellow">
          <AlertTriangle size={12}/>
          Retrospective research mode applies the LATEST profile/strategy versions to historical periods.
          Results will be labelled "Not Comparable" — never counted as true out-of-sample evidence.
        </div>
        <Switch checked={!!cfg.retrospective_mode} onCheckedChange={(v) => s("retrospective_mode", v)} data-testid="wf-retrospective"/>
      </div>
    </div>
  );
}

function ProgressBar({ job }) {
  if (!job) return null;
  const pct = Number(job.progress_pct || 0);
  return (
    <div className="grid-cell !p-3" data-testid="wf-job-progress">
      <div className="flex items-center gap-2">
        <Repeat size={12} className="text-[#0A84FF]"/>
        <span className="mono uppercase text-[10px] tracking-widest text-[#0A84FF]" data-testid="wf-job-status">
          {(job.status || "").replace(/_/g, " ")}
        </span>
        <span className="ml-auto mono text-[10px] text-muted-foreground">
          {job.current_window_idx ?? 0} / {job.total_windows ?? 0} windows · {pct.toFixed(0)}%
        </span>
      </div>
      <div className="mt-2 h-1.5 bg-secondary/40 relative overflow-hidden">
        <div className="absolute inset-y-0 left-0 bg-[#0A84FF] transition-all duration-300" style={{ width: `${pct}%` }}/>
      </div>
      {job.status === "failed" && (
        <div className="text-[11px] text-signal-red mt-2" data-testid="wf-job-error">
          {job.error_code || "error"} — {job.error_message || "Unknown error"}
        </div>
      )}
      {job.status === "cancelled" && (
        <div className="text-[11px] text-muted-foreground mt-2">Job cancelled by user.</div>
      )}
    </div>
  );
}

function SummaryCards({ summary }) {
  if (!summary || !Object.keys(summary).length) return null;
  const r = summary.robustness || "Insufficient Evidence";
  return (
    <div className="space-y-2" data-testid="wf-summary">
      <div className="flex items-center gap-2">
        <span className="mono uppercase text-[10px] tracking-widest text-muted-foreground">Walk-Forward Summary</span>
        <span className={`chip !text-[10px] uppercase ${ROBUSTNESS_TONE[r] || ""}`} data-testid="wf-robustness">{r}</span>
        {summary.retrospective_mode && (
          <span className="chip !text-[10px] uppercase border-signal-yellow text-signal-yellow">Retrospective</span>
        )}
        <span className={`chip !text-[10px] uppercase ${DQ_TONE[summary.data_quality_status] || ""}`} data-testid="wf-summary-dq">
          Data Quality · {summary.data_quality_status}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-px bg-border/60">
        {[
          ["Windows", `${summary.completed_windows ?? 0} / ${summary.total_windows ?? 0}`],
          ["Profitable Forward", `${summary.profitable_forward_windows ?? 0} (${(summary.pct_profitable_forward ?? 0).toFixed(1)}%)`],
          ["Beating Bench", `${summary.windows_beating_benchmark ?? 0} (${(summary.pct_beating_benchmark ?? 0).toFixed(1)}%)`],
          ["Avg Fwd Return", pct(summary.avg_forward_return_pct)],
          ["Avg Degradation", pct(summary.avg_degradation_pct)],
          ["Avg Fwd PF", num(summary.avg_forward_profit_factor)],
          ["Avg Fwd Sharpe", num(summary.avg_forward_sharpe)],
          ["Best Window", summary.best_forward_window ? `#${summary.best_forward_window.window_idx} · ${pct(summary.best_forward_window.return_pct)}` : "N/A"],
          ["Worst Window", summary.worst_forward_window ? `#${summary.worst_forward_window.window_idx} · ${pct(summary.worst_forward_window.return_pct)}` : "N/A"],
          ["Regime Coverage", (summary.regime_coverage || []).join(", ") || "N/A"],
        ].map(([k, v]) => (
          <div key={k} className="p-2 bg-card/40 min-w-0">
            <div className="label-cap">{k}</div>
            <div className="mono text-sm truncate" title={String(v)}>{v}</div>
          </div>
        ))}
      </div>
      <div className="text-[10px] mono uppercase text-muted-foreground flex flex-wrap gap-2">
        <span>Strategy consistency: {summary.strategy_version_consistency ? "✓" : "✗"}</span>
        <span>Model consistency: {summary.model_version_consistency ? "✓" : "✗"}</span>
        <span>Prompt consistency: {summary.prompt_version_consistency ? "✓" : "✗"}</span>
        <span>Profile consistency: {summary.profile_version_consistency ? "✓" : "✗"}</span>
      </div>
    </div>
  );
}

function WindowsTable({ results }) {
  if (!results?.length) return null;
  return (
    <div className="grid-cell !p-0 overflow-auto" data-testid="wf-windows-table">
      <table className="w-full text-xs">
        <thead className="bg-card/60 border-b border-border/60 text-[10px] uppercase mono text-muted-foreground tracking-widest">
          <tr>
            <th className="p-2 text-left">#</th>
            <th className="p-2 text-left">Training</th>
            <th className="p-2 text-left">Forward</th>
            <th className="p-2 text-left">Regime</th>
            <th className="p-2 text-right">Train N</th>
            <th className="p-2 text-right">Fwd N</th>
            <th className="p-2 text-right">Train Ret</th>
            <th className="p-2 text-right">Fwd Ret</th>
            <th className="p-2 text-right">Bench</th>
            <th className="p-2 text-right">Alpha</th>
            <th className="p-2 text-right">Degrad</th>
            <th className="p-2 text-right">Fwd PF</th>
            <th className="p-2 text-right">Fwd Sharpe</th>
            <th className="p-2 text-right">Fwd MaxDD</th>
            <th className="p-2 text-left">Data Q</th>
            <th className="p-2 text-left">Status</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.window_idx} className="border-b border-border/40 hover:bg-secondary/20"
                data-testid={`wf-window-${r.window_idx}`}>
              <td className="p-2 mono">{r.window_idx}</td>
              <td className="p-2 mono text-[10px]">{r.training_start}<br/>{r.training_end}</td>
              <td className="p-2 mono text-[10px]">{r.forward_start}<br/>{r.forward_end}</td>
              <td className="p-2 mono text-[10px]">{r.primary_regime || "N/A"}</td>
              <td className="p-2 mono text-right">{r.training_trade_count}</td>
              <td className="p-2 mono text-right">{r.forward_trade_count}</td>
              <td className="p-2 mono text-right">{pct(r.training_return_pct)}</td>
              <td className={`p-2 mono text-right ${r.forward_return_pct == null ? "" : r.forward_return_pct >= 0 ? "pnl-up" : "pnl-down"}`}>{pct(r.forward_return_pct)}</td>
              <td className="p-2 mono text-right">{pct(r.benchmark_return_pct)}</td>
              <td className={`p-2 mono text-right ${r.forward_alpha_pct == null ? "" : r.forward_alpha_pct >= 0 ? "pnl-up" : "pnl-down"}`}>{pct(r.forward_alpha_pct)}</td>
              <td className="p-2 mono text-right">{pct(r.performance_degradation_pct)}</td>
              <td className="p-2 mono text-right">{num(r.forward_profit_factor)}</td>
              <td className="p-2 mono text-right">{num(r.forward_sharpe)}</td>
              <td className="p-2 mono text-right">{pct(r.forward_max_dd_pct)}</td>
              <td className="p-2"><span className={`chip !text-[9px] uppercase ${DQ_TONE[r.data_quality] || ""}`}>{r.data_quality}</span></td>
              <td className={`p-2 mono text-[11px] ${STATUS_TONE[r.result_status] || ""}`}>{r.result_status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function download(name, mime, content) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
}

export default function WalkForwardSection() {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const [activeCfg, setActiveCfg] = useState(null);
  const [cfg, setCfg] = useState({ ...DEFAULT_CFG });
  const [saving, setSaving] = useState(false);
  const [starting, setStarting] = useState(false);
  const [activeJobId, setActiveJobId] = useState(null);

  const { data: cfgData, refetch: refetchCfgs } = useQuery({
    queryKey: ["wf", "configs"],
    queryFn: async () => (await api.get("/walkforward/configs")).data,
  });
  const configs = cfgData?.configs || [];

  const { data: jobsData, refetch: refetchJobs } = useQuery({
    queryKey: ["wf", "jobs", activeCfg?.id],
    queryFn: async () => activeCfg
      ? (await api.get("/walkforward/jobs", { params: { config_id: activeCfg.id } })).data
      : { jobs: [] },
    enabled: !!activeCfg,
  });
  const jobs = jobsData?.jobs || [];
  const latestJob = jobs[0];

  const { data: resultsData, refetch: refetchResults } = useQuery({
    queryKey: ["wf", "results", activeJobId || latestJob?.id],
    queryFn: async () => {
      const jid = activeJobId || latestJob?.id;
      if (!jid) return null;
      return (await api.get(`/walkforward/jobs/${jid}/results`)).data;
    },
    enabled: !!(activeJobId || latestJob?.id),
    refetchInterval: (q) => {
      const status = q.state.data?.job?.status;
      if (!status) return false;
      return ["completed", "failed", "cancelled"].includes(status) ? false : 2000;
    },
  });

  useEffect(() => {
    if (configs.length && !activeCfg) selectConfig(configs[0]);
  }, [configs]);// eslint-disable-line react-hooks/exhaustive-deps

  function selectConfig(c) {
    setActiveCfg(c);
    setCfg({ ...DEFAULT_CFG, ...c });
    setActiveJobId(null);
  }
  function newConfig() {
    setActiveCfg(null);
    setCfg({ ...DEFAULT_CFG });
    setActiveJobId(null);
  }
  async function deleteConfig(c) {
    const ok = await confirm.confirm({
      variant: "destructive", title: `Delete '${c.name}'?`,
      description: "This cannot be undone.", confirmLabel: "Delete", testId: `wf-delete-${c.id}`,
    });
    if (!ok) return;
    try {
      await api.delete(`/walkforward/configs/${c.id}`);
      toast.success("Configuration deleted");
      const list = (await refetchCfgs()).data?.configs || [];
      if (activeCfg?.id === c.id) list.length ? selectConfig(list[0]) : newConfig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  }
  async function saveConfig() {
    if (!cfg.name?.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (activeCfg) {
        const { data } = await api.put(`/walkforward/configs/${activeCfg.id}`, cfg);
        toast.success("Configuration updated");
        setActiveCfg(data);
      } else {
        const { data } = await api.post(`/walkforward/configs`, cfg);
        toast.success(`Created — ${data.preview_windows} windows`);
        setActiveCfg(data);
      }
      await refetchCfgs();
      qc.invalidateQueries({ queryKey: ["wf"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }
  async function startJob() {
    if (!activeCfg) { toast.error("Save the configuration first"); return; }
    setStarting(true);
    try {
      const { data } = await api.post(`/walkforward/jobs/start`, { config_id: activeCfg.id });
      toast.success(`Job queued`);
      setActiveJobId(data.id);
      await refetchJobs();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Start failed");
    } finally { setStarting(false); }
  }
  async function cancelJob(job) {
    try {
      await api.post(`/walkforward/jobs/${job.id}/cancel`);
      toast.success("Cancellation requested");
      refetchJobs(); refetchResults();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  }

  const currentJob = resultsData?.job || latestJob;
  const results = resultsData?.results || [];
  const summary = resultsData?.summary || currentJob?.summary || {};

  const canCancel = currentJob && !["completed", "failed", "cancelled"].includes(currentJob.status);

  function exportCsv() {
    if (!results.length) return;
    const headers = Object.keys(results[0]);
    const rows = [headers.join(",")].concat(results.map((r) => headers.map((h) => csvEscape(r[h])).join(",")));
    download(`walkforward_${activeCfg?.name || "results"}.csv`, "text/csv", rows.join("\n"));
  }
  function exportJson() {
    if (!results.length) return;
    download(`walkforward_${activeCfg?.name || "results"}.json`, "application/json",
      JSON.stringify({ config: activeCfg, summary, results }, null, 2));
  }

  return (
    <div className="space-y-4" data-testid="wf-section">
      <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-4">
        <ConfigList
          configs={configs}
          activeId={activeCfg?.id}
          onSelect={selectConfig}
          onNew={newConfig}
          onDelete={deleteConfig}
        />
        <div className="space-y-3 min-w-0">
          <ConfigEditor cfg={cfg} setCfg={setCfg}/>
          <div className="flex flex-wrap gap-2 items-center">
            <button className="btn-outline" onClick={saveConfig} disabled={saving} data-testid="wf-save">
              {saving ? <Loader2 size={13} className="animate-spin"/> : <Save size={13}/>}
              {activeCfg ? "Update Configuration" : "Save Configuration"}
            </button>
            <button className="btn-primary" onClick={startJob} disabled={!activeCfg || starting} data-testid="wf-run">
              {starting ? <Loader2 size={13} className="animate-spin"/> : <Play size={13}/>}
              Run Validation
            </button>
            {canCancel && (
              <button className="btn-outline" onClick={() => cancelJob(currentJob)} data-testid="wf-cancel">
                <X size={13}/>Cancel Job
              </button>
            )}
            {results.length > 0 && (
              <>
                <button className="btn-ghost" onClick={exportCsv} data-testid="wf-export-csv">Export CSV</button>
                <button className="btn-ghost" onClick={exportJson} data-testid="wf-export-json">Export JSON</button>
              </>
            )}
          </div>
          {currentJob && <ProgressBar job={currentJob}/>}
          <SummaryCards summary={summary}/>
          <WindowsTable results={results}/>
          {jobs.length > 1 && (
            <div className="grid-cell !p-3" data-testid="wf-job-history">
              <div className="mono uppercase text-[10px] tracking-widest text-muted-foreground mb-2">Job History</div>
              <div className="space-y-1 text-xs">
                {jobs.map((j) => (
                  <button key={j.id}
                    onClick={() => setActiveJobId(j.id)}
                    className={`flex items-center gap-3 w-full text-left px-2 py-1 hover:bg-secondary/20 ${(activeJobId || latestJob?.id) === j.id ? "bg-secondary/40" : ""}`}
                    data-testid={`wf-job-${j.id}`}>
                    <span className="mono text-[10px] text-muted-foreground">{new Date(j.created_at).toLocaleString()}</span>
                    <span className="mono text-[10px] uppercase text-[#0A84FF]">{j.status}</span>
                    <span className="ml-auto mono text-[10px] text-muted-foreground">{j.total_windows} windows</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
